import { useState } from 'react';
import type { TargetView } from '../../shared/api';
import { GROUP_LABELS, scaleMap, type NutrientDef, type NutrientMap, type TotalsWithCoverage } from '../../shared/nutrients';
import { amount, defFor, unitLabel } from '../lib/format';
import { inListOrder, isPartial, rowShare } from '../lib/nutrition';

type Coverage = Pick<TotalsWithCoverage, 'known' | 'count'>;

interface Props {
  per100: NutrientMap;
  grams: number;
  defs: Map<string, NutrientDef>;
  tracked: string[];
  /** For sums (a recipe's ingredients): how many of `count` items had each nutrient. Partial ones show as "at least". */
  coverage?: Coverage;
}

/** Every known nutrient for this amount of a food or recipe: tracked ones first, the rest behind "More". */
export function NutritionPanel({ per100, grams, ...rest }: Props) {
  return <NutritionList amounts={scaleMap(per100, grams)} {...rest} />;
}

interface ListProps {
  /** The amounts to show, already worked out (a meal's totals, say). A missing key means no data. */
  amounts: NutrientMap;
  defs: Map<string, NutrientDef>;
  tracked: string[];
  /** For sums: how many of `count` items had each nutrient. Partial ones show as "at least". */
  coverage?: Coverage;
  /** What `coverage` counts, for the note under partial sums. */
  itemNoun?: 'ingredients' | 'foods';
  /** Daily targets: a tracked nutrient with one also shows what share of it these amounts are. */
  targets?: TargetView[];
}

/** Every nutrient in `amounts`: tracked ones first (with their share of a daily target, when given), the rest behind "More". */
export function NutritionList({ amounts, defs, tracked, coverage, itemNoun = 'ingredients', targets }: ListProps) {
  const [more, setMore] = useState(false);
  const keys = Object.keys(amounts);
  const trackedKeys = tracked.filter((k) => keys.includes(k));
  // In the nutrient list's order for every meal and food, not whichever food listed a nutrient first.
  const others = inListOrder(keys.filter((k) => !tracked.includes(k)), defs);
  const missingTracked = tracked.filter((k) => !keys.includes(k));
  const partial = (key: string) => coverage !== undefined && isPartial(key, coverage);
  const targetFor = new Map(targets?.map((t) => [t.key, t]));

  const row = (key: string) => {
    const def = defFor(key, defs);
    // Only tracked nutrients have targets; while settings load, a tracked extra can sit under "More".
    const words = targets && tracked.includes(key) ? rowShare(amounts[key], def, targetFor.get(key), partial(key)) : null;
    return (
      <div key={key} className="nlist__row small">
        <span className="nlist__name">{def.name}</span>
        <span className="nlist__value num">
          <span className="nlist__amount">
            {partial(key) && (
              <>
                <span className="sr-only">at least </span>
                <span aria-hidden="true">≥</span>
              </>
            )}
            <b>{amount(amounts[key], def)}</b> {unitLabel(def)}
          </span>
          {/* Rendered (empty) even without a share, so amounts line up when shares sit beside them. */}
          {targets && (
            <span className="nlist__share tiny muted">
              {words &&
                (words.spoken === words.text ? (
                  words.text
                ) : (
                  <>
                    <span className="sr-only">{words.spoken}</span>
                    <span aria-hidden="true">{words.text}</span>
                  </>
                ))}
            </span>
          )}
        </span>
      </div>
    );
  };

  return (
    <div className={targets ? 'nlist nlist--shares' : 'nlist'}>
      {trackedKeys.map(row)}
      {missingTracked.length > 0 && (
        <p className="tiny faint" style={{ marginTop: 6 }}>
          No data for {missingTracked.map((k) => defFor(k, defs).name).join(', ')}.
        </p>
      )}
      {others.length > 0 && (
        <>
          <button className="btn btn--quiet btn--xs" style={{ marginTop: 8 }} onClick={() => setMore((m) => !m)} aria-expanded={more}>
            {more ? 'Hide' : `More nutrients (${others.length})`}
          </button>
          {more &&
            Object.entries(GROUP_LABELS).map(([group, label]) => {
              const inGroup = others.filter((k) => defFor(k, defs).group === group);
              if (!inGroup.length) return null;
              return (
                <div key={group} style={{ marginTop: 10 }}>
                  <div className="ngroup__title">{label}</div>
                  {inGroup.map(row)}
                </div>
              );
            })}
        </>
      )}
      {(trackedKeys.some(partial) || (more && others.some(partial))) && (
        <p className="tiny faint" style={{ marginTop: 8 }}>
          ≥ means some {itemNoun} have no data for it, so the real amount may be higher.
        </p>
      )}
    </div>
  );
}
